import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AppHeader } from "../components/AppHeader";
import { Watermark } from "../components/Watermark";
import { apiFetch } from "../lib/api";
import { formatDateLong } from "../lib/dates";
import { useMe } from "../hooks/useMe";
import { useFeature } from "../hooks/useFeature";
import { resolveMembership } from "../hooks/useFamilyMembership";
import { EventForm } from "../components/EventForm";
import { EventDatePoll } from "../components/EventDatePoll";
import { EventGallery } from "../components/EventGallery";
import { RSVP_STATUSES, can, type Event, type EventRsvpListResponse, type RsvpStatus } from "@family/shared";

type Tab = "overview" | "gallery" | "faq" | "contact" | "rsvp";

function formatDateRange(e: Event): string {
  if (!e.eventDate) return "Date to be announced";
  const start = new Date(e.eventDate);
  const end = e.endDate ? new Date(e.endDate) : null;
  if (end && e.endDate !== e.eventDate) return `${formatDateLong(start)} – ${formatDateLong(end)}`;
  return formatDateLong(start);
}

function Countdown({ targetIso }: { targetIso: string }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = new Date(targetIso).getTime() - Date.now();
  if (diff <= 0) return null;
  const s = Math.floor(diff / 1000);
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const mins = Math.floor((s % 3600) / 60);
  const secs = s % 60;
  return (
    <div
      className="countdown-grid"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(4, minmax(60px, 1fr))",
        gap: "var(--s-2)",
        marginTop: "var(--s-3)",
      }}
    >
      {[
        ["Days", days],
        ["Hours", hours],
        ["Minutes", mins],
        ["Seconds", secs],
      ].map(([label, value]) => (
        <div
          key={label}
          className="card"
          style={{
            padding: "var(--s-3)",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: "1.75rem", fontWeight: 700 }}>{value}</div>
          <div
            className="text-subtle"
            style={{
              fontSize: "0.75rem",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            {label}
          </div>
        </div>
      ))}
    </div>
  );
}

export function EventDetailPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const meQuery = useMe();
  const primary = resolveMembership(meQuery.data);
  const slug = primary?.familySlug;
  const canManage = primary ? can(primary.role, "events:manage") : false;
  const liveFeature = useFeature("live_reunion") === true;
  const canHostLive = primary ? can(primary.role, "live:host") : false;
  const canRsvp = primary ? can(primary.role, "events:rsvp") : false;

  const isNew = eventId === "new";
  const [editing, setEditing] = useState(searchParams.get("edit") === "1" || isNew);

  const query = useQuery<Event>({
    queryKey: ["event", slug, eventId],
    queryFn: () => apiFetch<Event>(`/families/${slug}/events/${eventId}`),
    enabled: Boolean(slug && eventId && !isNew),
  });

  // Local guest-count input. Defaults to whatever is on the server. Only used
  // when status === "yes". Cleared/reset when the event id or my RSVP changes.
  const [guestCount, setGuestCount] = useState<number>(
    query.data?.myGuestCount ?? 1,
  );
  useEffect(() => {
    if (query.data) setGuestCount(query.data.myGuestCount ?? 1);
  }, [query.data?.myGuestCount, eventId]);

  const rsvpMutation = useMutation<
    Event,
    Error,
    { status: RsvpStatus | null; guestCount?: number }
  >({
    mutationFn: ({ status, guestCount }) =>
      apiFetch<Event>(`/families/${slug}/events/${eventId}/rsvp`, {
        method: "PUT",
        body: JSON.stringify({ status, guestCount }),
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData<Event>(["event", slug, eventId], updated);
      void queryClient.invalidateQueries({ queryKey: ["events", slug] });
      void queryClient.invalidateQueries({
        queryKey: ["event-rsvps", slug, eventId],
      });
    },
  });

  const deleteMutation = useMutation<void, Error, void>({
    mutationFn: () =>
      apiFetch(`/families/${slug}/events/${eventId}`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["events", slug] });
      navigate("/events", { replace: true });
    },
  });

  const tab = (searchParams.get("tab") as Tab | null) ?? "overview";
  function setTab(t: Tab) {
    searchParams.set("tab", t);
    setSearchParams(searchParams, { replace: true });
  }

  function onEditSaved(saved: Event) {
    setEditing(false);
    if (isNew) {
      navigate(`/events/${saved.id}`, { replace: true });
    } else {
      queryClient.setQueryData(["event", slug, eventId], saved);
    }
    void queryClient.invalidateQueries({ queryKey: ["events", slug] });
  }

  if (!primary || !slug) {
    return (
      <>
        <AppHeader />
        <main className="page">
          <p className="text-muted">Loading…</p>
        </main>
      </>
    );
  }

  if (isNew) {
    if (!canManage) {
      return (
        <>
          <AppHeader />
          <main className="page">
            <p className="text-error">Admins only.</p>
          </main>
        </>
      );
    }
    return (
      <>
        <AppHeader />
        <main className="page stack-lg">
          <Link to="/events" className="text-muted" style={{ textDecoration: "none" }}>
            ← Back to events
          </Link>
          <h1>New event</h1>
          <EventForm
            familySlug={slug}
            onSaved={onEditSaved}
            onCancel={() => navigate("/events")}
          />
        </main>
      </>
    );
  }

  const event = query.data;
  const loading = query.isLoading;

  return (
    <>
      <AppHeader />
      <main className="page stack-lg">
        <Link to="/events" className="text-muted" style={{ textDecoration: "none" }}>
          ← Back to events
        </Link>

        {loading && <p className="text-muted">Loading…</p>}
        {query.error && (
          <p className="text-error">Error: {(query.error as Error).message}</p>
        )}

        {event && editing && (
          <EventForm
            familySlug={slug}
            event={event}
            onSaved={onEditSaved}
            onCancel={() => setEditing(false)}
          />
        )}

        {event && !editing && (
          <>
            {event.coverUrl && (
              <div
                style={{
                  position: "relative",
                  borderRadius: 8,
                  overflow: "hidden",
                  background: "var(--c-bg-alt)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  maxHeight: "min(70vh, 720px)",
                }}
              >
                <img
                  src={event.coverUrl}
                  alt=""
                  style={{
                    width: "100%",
                    height: "auto",
                    maxHeight: "min(70vh, 720px)",
                    objectFit: "contain",
                    display: "block",
                    pointerEvents: "none",
                  }}
                  draggable={false}
                  onContextMenu={(e) => e.preventDefault()}
                />
                {primary && (
                  <Watermark
                    config={primary.watermark}
                    variables={{
                      user: meQuery.data?.user.displayName ?? meQuery.data?.user.email ?? "",
                      family: primary.familyName,
                      phone: meQuery.data?.user.phone ?? null,
                      email: meQuery.data?.user.email ?? null,
                    }}
                  />
                )}
              </div>
            )}

            <div className="row-between">
              <div>
                <h1 style={{ margin: 0 }}>{event.title}</h1>
                <p className="text-muted" style={{ marginTop: 4 }}>
                  {formatDateRange(event)}
                  {event.city || event.country ? (
                    <>
                      {" · "}
                      {[event.city, event.country].filter(Boolean).join(", ")}
                    </>
                  ) : null}
                </p>
              </div>
              {canManage && (
                <div style={{ display: "flex", gap: "var(--s-2)" }}>
                  <button
                    className="btn-tertiary"
                    onClick={() => setEditing(true)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn-tertiary"
                    style={{ color: "var(--c-danger)" }}
                    disabled={deleteMutation.isPending}
                    onClick={() => {
                      if (window.confirm(`Delete "${event.title}"?`)) {
                        deleteMutation.mutate();
                      }
                    }}
                  >
                    {deleteMutation.isPending ? "Deleting…" : "Delete"}
                  </button>
                </div>
              )}
            </div>

            {event.liveEnabled && liveFeature && (event.liveInvited || canHostLive) && (
              <button
                type="button"
                onClick={() => navigate(`/events/${event.id}/live`)}
                style={{ fontSize: "1.2rem", minHeight: 56 }}
              >
                Join the live get-together
              </button>
            )}

            {/* Tabs */}
            <nav
              style={{
                display: "flex",
                gap: "var(--s-2)",
                borderBottom: "1px solid var(--c-border)",
                flexWrap: "wrap",
              }}
            >
              {(["overview", "gallery", "faq", "contact", "rsvp"] as Tab[]).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTab(t)}
                  style={{
                    background: "transparent",
                    color:
                      tab === t ? "var(--c-blue)" : "var(--c-text-muted)",
                    fontWeight: 700,
                    padding: "8px 14px",
                    minHeight: 40,
                    border: "none",
                    borderBottom:
                      tab === t ? "2px solid var(--c-blue)" : "2px solid transparent",
                    borderRadius: 0,
                    textTransform: "capitalize",
                  }}
                >
                  {t === "faq" ? "FAQ" : t}
                  {t === "rsvp" && event.rsvpEnabled && (
                    <span
                      className="badge"
                      style={{ marginLeft: 8, fontSize: "0.65rem" }}
                      title={`${event.attendingTotal} attending`}
                    >
                      {event.attendingTotal}
                    </span>
                  )}
                </button>
              ))}
            </nav>

            {tab === "overview" && (
              <section className="stack">
                {slug && (
                  <EventDatePoll
                    slug={slug}
                    event={event}
                    canVote={canRsvp}
                    canManage={canManage}
                  />
                )}
                {event.description && (
                  <p style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                    {event.description}
                  </p>
                )}
                {(event.location || event.venue) && (
                  <div className="card">
                    <strong>Location</strong>
                    <div className="text-muted" style={{ marginTop: 4 }}>
                      {[event.venue, event.location, event.city, event.country]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  </div>
                )}
                {event.eventDate && (
                  <Countdown targetIso={`${event.eventDate}T00:00:00Z`} />
                )}
              </section>
            )}

            {tab === "gallery" && primary && (
              <EventGallery
                familySlug={slug}
                eventId={event.id}
                role={primary.role}
                currentUserId={meQuery.data?.user.id ?? null}
                watermarkConfig={primary.watermark}
                watermarkVars={{
                  user:
                    meQuery.data?.user.displayName ??
                    meQuery.data?.user.email ??
                    meQuery.data?.user.phone ??
                    "",
                  family: primary.familyName ?? "",
                  phone: meQuery.data?.user.phone ?? null,
                  email: meQuery.data?.user.email ?? null,
                }}
              />
            )}

            {tab === "faq" && (
              <section className="stack">
                {event.faq.length === 0 ? (
                  <p className="text-muted">No FAQs yet.</p>
                ) : (
                  event.faq.map((item, i) => (
                    <div key={i} className="card">
                      <strong>{item.question}</strong>
                      <div
                        style={{
                          whiteSpace: "pre-wrap",
                          marginTop: 8,
                          lineHeight: 1.6,
                        }}
                      >
                        {item.answer}
                      </div>
                    </div>
                  ))
                )}
              </section>
            )}

            {tab === "contact" && (
              <section className="stack">
                {event.contactEmail ? (
                  <div className="card">
                    <strong>Email</strong>
                    <div style={{ marginTop: 4 }}>
                      <a href={`mailto:${event.contactEmail}`}>{event.contactEmail}</a>
                    </div>
                  </div>
                ) : (
                  <p className="text-muted">No contact email set.</p>
                )}
                {event.paymentUrl && (
                  <div className="card">
                    <strong>Contribute</strong>
                    <div style={{ marginTop: 4 }}>
                      <a
                        href={event.paymentUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        {event.paymentUrl}
                      </a>
                    </div>
                  </div>
                )}
              </section>
            )}

            {tab === "rsvp" && (
              <RsvpTab
                event={event}
                slug={slug}
                canRsvp={canRsvp}
                canManage={canManage}
                guestCount={guestCount}
                setGuestCount={setGuestCount}
                rsvpMutation={rsvpMutation}
              />
            )}

            {deleteMutation.error && (
              <p className="text-error">{deleteMutation.error.message}</p>
            )}
          </>
        )}
      </main>
    </>
  );
}

type RsvpMutation = ReturnType<
  typeof useMutation<
    Event,
    Error,
    { status: RsvpStatus | null; guestCount?: number }
  >
>;

interface RsvpTabProps {
  event: Event;
  slug: string;
  canRsvp: boolean;
  canManage: boolean;
  guestCount: number;
  setGuestCount: (n: number) => void;
  rsvpMutation: RsvpMutation;
}

function RsvpTab({
  event,
  slug,
  canRsvp,
  canManage,
  guestCount,
  setGuestCount,
  rsvpMutation,
}: RsvpTabProps) {
  if (!event.rsvpEnabled) {
    return (
      <section className="stack">
        <p className="text-muted">RSVP is disabled for this event.</p>
      </section>
    );
  }

  const myCount = event.myGuestCount ?? 1;
  const summary = event.myRsvp
    ? event.myRsvp === "yes"
      ? `You're attending with ${myCount} ${myCount === 1 ? "person" : "people"} total.`
      : `You're currently marked as "${event.myRsvp}".`
    : "Let us know if you'll be there.";

  return (
    <section className="stack">
      <p className="text-muted">{summary}</p>

      <div style={{ display: "flex", gap: "var(--s-2)", flexWrap: "wrap" }}>
        {RSVP_STATUSES.map((status) => (
          <button
            key={status}
            type="button"
            disabled={!canRsvp || rsvpMutation.isPending}
            className={event.myRsvp === status ? "" : "btn-secondary"}
            onClick={() =>
              rsvpMutation.mutate({
                status,
                guestCount: status === "yes" ? guestCount : undefined,
              })
            }
            style={{ minWidth: 110 }}
          >
            {status === "yes"
              ? "Yes, I'll be there"
              : status === "no"
                ? "No, can't make it"
                : "Maybe"}
          </button>
        ))}
        {event.myRsvp && (
          <button
            type="button"
            className="btn-tertiary"
            disabled={rsvpMutation.isPending}
            onClick={() => rsvpMutation.mutate({ status: null })}
          >
            Clear
          </button>
        )}
      </div>

      {/* Guest count picker — only relevant for "yes". Visible whenever the
       *  user can RSVP, so they can adjust before clicking Yes. */}
      {canRsvp && (
        <div
          className="card"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--s-3)",
            flexWrap: "wrap",
          }}
        >
          <label htmlFor="rsvp-count" style={{ fontWeight: 600, margin: 0 }}>
            How many people are you bringing (including yourself)?
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: "var(--s-2)" }}>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setGuestCount(Math.max(1, guestCount - 1))}
              disabled={rsvpMutation.isPending || guestCount <= 1}
              aria-label="Decrease attendees"
              style={{ minWidth: 36, padding: "4px 10px" }}
            >
              −
            </button>
            <input
              id="rsvp-count"
              type="number"
              min={1}
              max={50}
              value={guestCount}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10);
                if (!Number.isNaN(n)) setGuestCount(Math.min(50, Math.max(1, n)));
              }}
              disabled={rsvpMutation.isPending}
              style={{
                width: 64,
                textAlign: "center",
                padding: "6px 8px",
              }}
            />
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setGuestCount(Math.min(50, guestCount + 1))}
              disabled={rsvpMutation.isPending || guestCount >= 50}
              aria-label="Increase attendees"
              style={{ minWidth: 36, padding: "4px 10px" }}
            >
              +
            </button>
          </div>
          {event.myRsvp === "yes" && guestCount !== myCount && (
            <button
              type="button"
              onClick={() =>
                rsvpMutation.mutate({ status: "yes", guestCount })
              }
              disabled={rsvpMutation.isPending}
            >
              Update count
            </button>
          )}
        </div>
      )}

      {rsvpMutation.error && (
        <p className="text-error">{rsvpMutation.error.message}</p>
      )}

      <div
        style={{
          display: "flex",
          gap: "var(--s-4)",
          marginTop: "var(--s-3)",
          flexWrap: "wrap",
        }}
      >
        <span>
          <strong>{event.attendingTotal}</strong>{" "}
          <span className="text-muted">attending</span>
        </span>
        <span>
          <strong>{event.rsvpCounts.yes}</strong>{" "}
          <span className="text-muted">yes</span>
        </span>
        <span>
          <strong>{event.rsvpCounts.maybe}</strong>{" "}
          <span className="text-muted">maybe</span>
        </span>
        <span>
          <strong>{event.rsvpCounts.no}</strong>{" "}
          <span className="text-muted">no</span>
        </span>
      </div>

      {canManage && <AttendeeList slug={slug} eventId={event.id} />}
    </section>
  );
}

function AttendeeList({ slug, eventId }: { slug: string; eventId: string }) {
  const query = useQuery<EventRsvpListResponse>({
    queryKey: ["event-rsvps", slug, eventId],
    queryFn: () =>
      apiFetch<EventRsvpListResponse>(
        `/families/${slug}/events/${eventId}/rsvps`,
      ),
  });

  return (
    <section className="stack" style={{ marginTop: "var(--s-4)" }}>
      <h3 style={{ fontSize: "1rem", margin: 0 }}>
        Attendees{" "}
        <span className="text-muted" style={{ fontSize: "0.85rem" }}>
          (admin view)
        </span>
      </h3>
      {query.isLoading && <p className="text-muted">Loading…</p>}
      {query.error && (
        <p className="text-error">Error: {(query.error as Error).message}</p>
      )}
      {query.data && (
        <>
          <p className="text-muted" style={{ fontSize: "0.9rem" }}>
            <strong>{query.data.totals.attendingTotal}</strong> total people
            attending across <strong>{query.data.totals.yes}</strong> RSVPs.
          </p>
          {query.data.attendees.length === 0 ? (
            <p className="text-muted">No RSVPs yet.</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "0.9rem",
                }}
              >
                <thead>
                  <tr style={{ textAlign: "left" }}>
                    <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--c-border)" }}>
                      Name
                    </th>
                    <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--c-border)" }}>
                      Status
                    </th>
                    <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--c-border)" }}>
                      Headcount
                    </th>
                    <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--c-border)" }}>
                      Contact
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {query.data.attendees.map((a) => (
                    <tr key={a.userId}>
                      <td style={{ padding: "8px 12px", borderBottom: "1px solid var(--c-border)" }}>
                        {a.displayName ?? "—"}
                      </td>
                      <td style={{ padding: "8px 12px", borderBottom: "1px solid var(--c-border)" }}>
                        <span
                          className={`badge ${a.status === "yes" ? "badge--admin" : ""}`}
                        >
                          {a.status}
                        </span>
                      </td>
                      <td style={{ padding: "8px 12px", borderBottom: "1px solid var(--c-border)" }}>
                        {a.status === "yes" ? a.guestCount : "—"}
                      </td>
                      <td
                        className="text-muted"
                        style={{ padding: "8px 12px", borderBottom: "1px solid var(--c-border)" }}
                      >
                        {a.email ?? a.phone ?? "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}
